/**
 * Validasi pengajuan koreksi absensi — MURNI, tanpa React dan tanpa jam sistem.
 *
 * Validasi di sini **memandu, tidak menegakkan**. Server tetap satu-satunya yang memutuskan:
 * `CreateCorrectionDto` menuntut `reason` 1–1000 karakter dan `correctionType` dari daftar
 * tertutup. Gunanya menjawab lebih awal "kalau dikirim sekarang, apa yang akan ditolak" —
 * di lapangan, satu perjalanan bolak-balik ke server dengan sinyal buruk itu mahal.
 *
 * Menghapus modul ini tidak membuat satu pun pengajuan tidak sah menjadi diterima.
 */

import type { AttendanceCorrection } from '../api/types';

export const CORRECTION_TYPES = [
  'MISSING_CHECK_IN',
  'MISSING_CHECK_OUT',
  'WRONG_TIME',
  'WRONG_WORK_MODE',
  'MARK_ON_LEAVE',
  'MARK_HALF_DAY_LEAVE',
] as const;

export type CorrectionType = (typeof CORRECTION_TYPES)[number];

const CORRECTION_TYPE_LABEL: Readonly<Record<CorrectionType, string>> = {
  MISSING_CHECK_IN: 'Lupa absen masuk',
  MISSING_CHECK_OUT: 'Lupa absen pulang',
  WRONG_TIME: 'Jam tercatat salah',
  WRONG_WORK_MODE: 'Mode kerja salah',
  // Bukan lagi "Tandai sebagai cuti": sejak jenis izin ada, opsi ini mencakup empat kategori
  // (Sakit, Izin pribadi, Cuti melahirkan, Cuti tahunan) — label lama akan menyesatkan orang
  // yang mengajukan izin sakit, seolah ia sedang mengajukan cuti tahunannya.
  MARK_ON_LEAVE: 'Cuti / izin',
  MARK_HALF_DAY_LEAVE: 'Izin setengah hari',
};

export function formatCorrectionType(type: string): string {
  return CORRECTION_TYPE_LABEL[type as CorrectionType] ?? type;
}

/**
 * Jenis izin — hanya bermakna saat `correctionType = MARK_ON_LEAVE` (ketetapan Owner
 * 24 Agustus 2026). Cerminan `workforce_leave_type` di server.
 *
 * "Izin setengah hari" BUKAN salah satu dari empat ini, dan punya tipe koreksinya sendiri
 * (`MARK_HALF_DAY_LEAVE`, lihat `HALF_DAY_PARTS` di bawah) — ia hari HADIR dengan jam berkurang,
 * bukan hari kosong seperti keempat jenis ini.
 */
export const LEAVE_TYPES = ['ANNUAL', 'SICK', 'PERSONAL', 'MATERNITY'] as const;

export type LeaveType = (typeof LEAVE_TYPES)[number];

const LEAVE_TYPE_LABEL: Readonly<Record<LeaveType, string>> = {
  ANNUAL: 'Cuti tahunan',
  SICK: 'Sakit',
  PERSONAL: 'Izin pribadi',
  MATERNITY: 'Cuti melahirkan',
};

export function formatLeaveType(type: string): string {
  return LEAVE_TYPE_LABEL[type as LeaveType] ?? type;
}

/**
 * Bagian hari yang diizinkan — hanya bermakna saat `correctionType = MARK_HALF_DAY_LEAVE`
 * (ketetapan Owner 24 Agustus 2026).
 *
 * Bukan sekadar label: PAGI memengaruhi status LATE (kurir datang belakangan), sedangkan SIANG
 * tidak memengaruhi status apa pun di sistem sekarang — tidak ada penalti untuk absen pulang
 * lebih awal. Keduanya tetap dicatat sebagai KETERANGAN, bukan mengubah status hari (ketetapan
 * Owner: status tetap apa adanya, keterangan tampil di sampingnya).
 */
export const HALF_DAY_PARTS = ['MORNING', 'AFTERNOON'] as const;

export type HalfDayPart = (typeof HALF_DAY_PARTS)[number];

const HALF_DAY_PART_LABEL: Readonly<Record<HalfDayPart, string>> = {
  MORNING: 'Pagi',
  AFTERNOON: 'Siang',
};

export function formatHalfDayPart(part: string): string {
  return HALF_DAY_PART_LABEL[part as HalfDayPart] ?? part;
}

/**
 * Izin setengah hari yang SUDAH DISETUJUI, per tanggal — untuk keterangan di layar Riwayat dan
 * Detail Hari (ketetapan Owner: status hari tetap apa adanya, keterangan tampil di sampingnya).
 *
 * Dibaca LANGSUNG dari baris koreksi APPROVED, bukan dari `AttendanceDay` — `MARK_HALF_DAY_LEAVE`
 * sengaja tidak menyentuh `attendance_days` sama sekali (lihat komentar enum di server), jadi
 * baris koreksi itu sendiri satu-satunya sumber kebenarannya. Pemanggil karena itu perlu
 * mengambil koreksi TANPA filter status — memfilter ke PENDING saja di jaringan akan membuat
 * fungsi ini tidak pernah menemukan satu pun yang sudah disetujui.
 */
export function approvedHalfDayLeaveByDate(
  corrections: readonly AttendanceCorrection[],
): ReadonlyMap<string, HalfDayPart> {
  const hasil = new Map<string, HalfDayPart>();
  for (const koreksi of corrections) {
    if (koreksi?.correctionType !== 'MARK_HALF_DAY_LEAVE') continue;
    if (koreksi.status !== 'APPROVED') continue;
    if (typeof koreksi.workDate !== 'string') continue;
    const part = koreksi.requestedPayload?.part;
    if (part !== 'MORNING' && part !== 'AFTERNOON') continue;
    hasil.set(koreksi.workDate, part);
  }
  return hasil;
}

/** Batas `reason` mengikuti `CreateCorrectionDto` — bukan angka yang dikarang di klien. */
export const REASON_MAX_LENGTH = 1000;

export interface CorrectionFormValues {
  readonly workDate: string;
  readonly correctionType: string;
  readonly reason: string;
  /** Jam usulan `HH:MM`; hanya bermakna untuk tipe yang menyentuh waktu. */
  readonly proposedTime: string;
  /** Hanya bermakna untuk `MARK_ON_LEAVE`. Kosong berarti belum dipilih. */
  readonly leaveType: string;
  /** Hanya bermakna untuk `MARK_HALF_DAY_LEAVE`. Kosong berarti belum dipilih. */
  readonly halfDayPart: string;
  /** Absen mana yang dikoreksi — hanya untuk `WRONG_TIME` dan `WRONG_WORK_MODE`. */
  readonly eventType: string;
  /** Mode kerja yang benar — hanya untuk `WRONG_WORK_MODE`. */
  readonly workMode: string;
}

export interface CorrectionFormProblem {
  readonly field:
    | 'workDate'
    | 'correctionType'
    | 'reason'
    | 'proposedTime'
    | 'leaveType'
    | 'halfDayPart'
    | 'eventType'
    | 'workMode';
  readonly message: string;
}

/** Tipe yang menuntut jam usulan — sisanya tidak menampilkan isian itu sama sekali. */
export function requiresProposedTime(correctionType: string): boolean {
  return (
    correctionType === 'MISSING_CHECK_IN' ||
    correctionType === 'MISSING_CHECK_OUT' ||
    correctionType === 'WRONG_TIME'
  );
}

/**
 * Tipe yang menuntut "absen mana yang dikoreksi".
 *
 * `MISSING_CHECK_IN`/`MISSING_CHECK_OUT` TIDAK termasuk — namanya sudah menyebutnya, dan
 * menanyakan lagi berarti isian yang jawabannya sudah diketahui.
 */
export function requiresEventType(correctionType: string): boolean {
  return correctionType === 'WRONG_TIME' || correctionType === 'WRONG_WORK_MODE';
}

/** Hanya `WRONG_WORK_MODE` — itu justru yang dikoreksi. */
export function requiresWorkMode(correctionType: string): boolean {
  return correctionType === 'WRONG_WORK_MODE';
}

export const WORK_MODES = ['WFO', 'WFH', 'FIELD_WORK', 'BUSINESS_TRIP'] as const;
export const WORK_MODE_LABEL: Readonly<Record<string, string>> = {
  WFO: 'Di kantor (WFO)',
  WFH: 'Dari rumah (WFH)',
  FIELD_WORK: 'Kerja lapangan',
  BUSINESS_TRIP: 'Perjalanan dinas',
};

/** Satu-satunya tipe yang menuntut jenis izin — sisanya tidak menampilkan isian itu sama sekali. */
export function requiresLeaveType(correctionType: string): boolean {
  return correctionType === 'MARK_ON_LEAVE';
}

/** Satu-satunya tipe yang menuntut bagian hari — sisanya tidak menampilkan isian itu sama sekali. */
export function requiresHalfDayPart(correctionType: string): boolean {
  return correctionType === 'MARK_HALF_DAY_LEAVE';
}

/**
 * Jenis izin BOLEH mengajukan untuk hari yang belum tiba — sisanya (empat tipe yang memperbaiki
 * fakta yang sudah terjadi) tidak: "lupa check-in besok" tidak bermakna, belum ada apa pun yang
 * bisa lupa terjadi. Cerminan `allowsFutureWorkDate` di server — server tetap yang memutuskan.
 */
export function allowsFutureWorkDate(correctionType: string): boolean {
  return correctionType === 'MARK_ON_LEAVE' || correctionType === 'MARK_HALF_DAY_LEAVE';
}

const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Masalah yang bisa dilihat sebelum mengirim, terurut sesuai urutan isian di layar.
 *
 * `today` diterima sebagai parameter, bukan dibaca dari `new Date()`: hari kerja ditentukan
 * timezone ORGANISASI, dan modul murni tidak boleh memutuskan "hari ini" sendiri. Memanggil
 * `new Date()` di sini akan membuat kurir yang ponselnya masih di zona lain ditolak untuk
 * tanggal yang sebenarnya sah.
 */
export function validateCorrectionForm(
  values: CorrectionFormValues,
  today: string,
): readonly CorrectionFormProblem[] {
  const problems: CorrectionFormProblem[] = [];

  if (!DATE_PATTERN.test(values.workDate)) {
    problems.push({ field: 'workDate', message: 'Pilih tanggal yang ingin dikoreksi.' });
  } else if (values.workDate > today && !allowsFutureWorkDate(values.correctionType)) {
    // Perbandingan string sah untuk `YYYY-MM-DD` dan menghindari Date sepenuhnya.
    problems.push({
      field: 'workDate',
      message: 'Tanggal belum terjadi. Koreksi hanya untuk hari yang sudah lewat atau hari ini.',
    });
  }

  if (!CORRECTION_TYPES.includes(values.correctionType as CorrectionType)) {
    problems.push({ field: 'correctionType', message: 'Pilih jenis koreksi.' });
  }

  /*
   * Dua isian yang dulu tidak pernah divalidasi karena memang tidak pernah ada — dan
   * ketiadaannya membuat server menolak kirimannya dengan galat mentah.
   */
  if (
    requiresEventType(values.correctionType) &&
    values.eventType !== 'CHECK_IN' &&
    values.eventType !== 'CHECK_OUT'
  ) {
    problems.push({
      field: 'eventType',
      message: 'Pilih absen mana yang dikoreksi — masuk atau pulang.',
    });
  }

  if (
    requiresWorkMode(values.correctionType) &&
    !(WORK_MODES as readonly string[]).includes(values.workMode)
  ) {
    problems.push({ field: 'workMode', message: 'Pilih mode kerja yang benar.' });
  }

  if (requiresProposedTime(values.correctionType) && !TIME_PATTERN.test(values.proposedTime)) {
    problems.push({
      field: 'proposedTime',
      message: 'Isi jam usulan dalam format 24 jam, mis. 08:15.',
    });
  }

  if (
    requiresLeaveType(values.correctionType) &&
    !LEAVE_TYPES.includes(values.leaveType as LeaveType)
  ) {
    problems.push({ field: 'leaveType', message: 'Pilih jenis izin.' });
  }

  if (
    requiresHalfDayPart(values.correctionType) &&
    !HALF_DAY_PARTS.includes(values.halfDayPart as HalfDayPart)
  ) {
    problems.push({ field: 'halfDayPart', message: 'Pilih bagian hari.' });
  }

  const reason = values.reason.trim();
  if (reason.length === 0) {
    // Alasan WAJIB. Approval tanpa alasan memaksa manajer menebak, dan pengajuan tanpa alasan
    // adalah yang paling sering ditolak lalu diajukan ulang — dua kali kerja untuk semua orang.
    problems.push({ field: 'reason', message: 'Tuliskan alasan koreksi.' });
  } else if (reason.length > REASON_MAX_LENGTH) {
    problems.push({
      field: 'reason',
      message: `Alasan terlalu panjang (maksimum ${REASON_MAX_LENGTH} karakter).`,
    });
  }

  return problems;
}

export function isCorrectionFormValid(values: CorrectionFormValues, today: string): boolean {
  return validateCorrectionForm(values, today).length === 0;
}

/**
 * `requestedPayload` yang dikirim ke server.
 *
 * Bentuknya sengaja minimal: hanya yang relevan bagi tipe koreksinya. Mengirim field kosong
 * untuk tipe yang tidak memakainya membuat pembaca audit di kemudian hari mengira ada usulan
 * yang tidak pernah diajukan.
 */
export function buildRequestedPayload(values: CorrectionFormValues): Record<string, unknown> {
  if (requiresLeaveType(values.correctionType)) {
    return { leaveType: values.leaveType };
  }
  if (requiresHalfDayPart(values.correctionType)) {
    return { part: values.halfDayPart };
  }

  /*
   * Bentuk yang BENAR-BENAR diterima server, dan hanya yang relevan bagi jenisnya.
   *
   * Sebelumnya `WRONG_WORK_MODE` mengirim `{}` kosong dan `WRONG_TIME` mengirim jam tanpa
   * menyebut absen mana yang salah. Keduanya ditolak server, dan tidak ada satu pun uji yang
   * memegang kedua sisi sekaligus — empat dari enam jenis koreksi tidak pernah berhasil.
   *
   * Klien TIDAK menyusun timestamp: ia mengirim jam lokal apa adanya, dan server yang
   * menggabungkannya dengan tanggal kerja serta zona waktu kebijakan.
   */
  const payload: Record<string, unknown> = {};
  if (requiresProposedTime(values.correctionType)) {
    payload.proposedTime = values.proposedTime;
  }
  if (requiresEventType(values.correctionType)) {
    payload.eventType = values.eventType;
  }
  if (requiresWorkMode(values.correctionType)) {
    payload.workMode = values.workMode;
  }
  return payload;
}
